import { useState, useRef } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import {
  LayoutDashboard, Users, Banknote, Monitor, UserCog,
  ShieldAlert, History, Snowflake, Activity, BarChart3,
  FileText, Wrench, Settings, Menu, X, Bell, ChevronRight,
  LogOut, ArrowLeft, ArrowLeftRight, MessageSquare, Sun, Moon,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { motion, AnimatePresence } from 'framer-motion'
import { ScrollContext } from '@/components/shared/ScrollContext'
import { useThemeStore } from '@/stores/themeStore'

const navGroups = [
  {
    label: '總覽',
    items: [
      { path: '/admin', icon: LayoutDashboard, label: '儀表板', badge: null },
    ],
  },
  {
    label: '營運管理',
    items: [
      { path: '/admin/users', icon: Users, label: '用戶管理', badge: null },
      { path: '/admin/transactions', icon: ArrowLeftRight, label: '交易紀錄', badge: null },
      { path: '/admin/withdrawals', icon: Banknote, label: '提款管理', badge: '3' },
      { path: '/admin/rooms', icon: Monitor, label: '房間監控', badge: null },
      { path: '/admin/disputes', icon: MessageSquare, label: '申訴管理', badge: '2' },
    ],
  },
  {
    label: '代理系統',
    items: [
      { path: '/admin/agents', icon: UserCog, label: '代理管理', badge: null },
    ],
  },
  {
    label: '安全中心',
    items: [
      { path: '/admin/fraud', icon: ShieldAlert, label: '詐欺警報', badge: '2' },
      { path: '/admin/frozen-pots', icon: Snowflake, label: '凍結彩池', badge: '1' },
    ],
  },
  {
    label: '遊戲紀錄',
    items: [
      { path: '/admin/hands', icon: History, label: '牌局紀錄', badge: null },
    ],
  },
  {
    label: '系統',
    items: [
      { path: '/admin/health', icon: Activity, label: '系統狀態', badge: null },
      { path: '/admin/reports', icon: BarChart3, label: '報表分析', badge: null },
      { path: '/admin/audit', icon: FileText, label: '操作日誌', badge: null },
    ],
  },
  {
    label: '設定',
    items: [
      { path: '/admin/maintenance', icon: Wrench, label: '維護模式', badge: null },
      { path: '/admin/config', icon: Settings, label: '系統設定', badge: null },
    ],
  },
]

export function AdminLayout() {
  const [drawerOpen, setDrawerOpen] = useState(false)
  const location = useLocation()
  const navigate = useNavigate()
  const scrollRef = useRef<HTMLElement>(null)

  const currentItem = navGroups
    .flatMap((g) => g.items)
    .find((item) => item.path === location.pathname || (item.path !== '/admin' && location.pathname.startsWith(item.path)))

  return (
    <div className="flex flex-col h-full bg-background">
      {/* Top Header Bar */}
      <header className="flex-shrink-0 relative z-40 bg-background/95 backdrop-blur-xl border-b border-foreground/[0.08] safe-area-top">
        <div className="flex items-center justify-between h-14 px-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setDrawerOpen(true)}
              className="w-10 h-10 flex items-center justify-center rounded-xl hover:bg-foreground/5 transition-colors"
            >
              <Menu className="w-5 h-5 text-muted-foreground" />
            </button>
            <div>
              <h4 className="text-sm font-semibold text-foreground">
                {currentItem?.label || '管理後台'}
              </h4>
              <p className="text-[10px] text-gold font-bold uppercase tracking-wider">Admin Panel</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                const { resolvedTheme, setTheme } = useThemeStore.getState()
                setTheme(resolvedTheme === 'dark' ? 'light' : 'dark')
              }}
              className="w-10 h-10 flex items-center justify-center rounded-xl hover:bg-foreground/5 transition-colors"
            >
              {useThemeStore((s) => s.resolvedTheme) === 'dark'
                ? <Sun className="w-5 h-5 text-muted-foreground" />
                : <Moon className="w-5 h-5 text-muted-foreground" />
              }
            </button>
            <button className="relative w-10 h-10 flex items-center justify-center rounded-xl hover:bg-foreground/5 transition-colors">
              <Bell className="w-5 h-5 text-muted-foreground" />
              <span className="absolute top-2 right-2 w-2 h-2 bg-red-500 rounded-full" />
            </button>
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-gold to-gold-deep flex items-center justify-center">
              <span className="text-xs font-bold text-primary-foreground">SA</span>
            </div>
          </div>
        </div>

        {/* Quick Stats Banner */}
        <div className="flex items-center gap-4 px-4 py-2 border-t border-foreground/[0.04] overflow-x-auto scrollbar-hide">
          {[
            { label: '活躍房間', value: '12', color: 'text-emerald-400' },
            { label: '線上玩家', value: '47', color: 'text-blue-400' },
            { label: '待審提款', value: '3', color: 'text-amber-400' },
            { label: '今日營收', value: '$1,847', color: 'text-gold' },
          ].map((stat) => (
            <div key={stat.label} className="flex items-center gap-1.5 flex-shrink-0">
              <span className="text-[10px] text-muted-foreground">{stat.label}</span>
              <span className={cn('text-xs font-bold font-mono', stat.color)}>{stat.value}</span>
            </div>
          ))}
        </div>
      </header>

      {/* Main Content */}
      <ScrollContext.Provider value={scrollRef}>
        <main ref={scrollRef} className="flex-1 overflow-y-auto scrollbar-hide">
          <Outlet />
        </main>
      </ScrollContext.Provider>

      {/* Navigation Drawer Overlay */}
      <AnimatePresence>
        {drawerOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setDrawerOpen(false)}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
            />
            <motion.div
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ type: 'spring', stiffness: 400, damping: 35 }}
              className="fixed top-0 left-0 bottom-0 w-72 bg-surface-elevated border-r border-foreground/[0.08] z-50 flex flex-col safe-area-top"
            >
              {/* Drawer Header */}
              <div className="flex items-center justify-between px-4 h-14 border-b border-foreground/[0.06]">
                <div className="flex items-center gap-2">
                  <span className="text-gold text-lg font-bold">PWG</span>
                  <span className="text-foreground text-sm font-semibold">管理後台</span>
                </div>
                <button
                  onClick={() => setDrawerOpen(false)}
                  className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-foreground/5"
                >
                  <X className="w-4 h-4 text-muted-foreground" />
                </button>
              </div>

              {/* Admin Info */}
              <div className="px-4 py-3 border-b border-foreground/[0.04]">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-gold to-gold-deep flex items-center justify-center">
                    <span className="text-sm font-bold text-background">SA</span>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground">Super Admin</p>
                    <div className="flex items-center gap-1.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                      <span className="text-[10px] text-emerald-400 font-medium">線上</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Nav Items */}
              <nav className="flex-1 overflow-y-auto py-2 scrollbar-hide">
                {navGroups.map((group) => (
                  <div key={group.label} className="mb-1">
                    <p className="px-4 py-2 text-[10px] font-bold text-muted-foreground uppercase tracking-wider">
                      {group.label}
                    </p>
                    {group.items.map((item) => {
                      const isActive = location.pathname === item.path || (item.path !== '/admin' && location.pathname.startsWith(item.path))
                      const Icon = item.icon
                      return (
                        <button
                          key={item.path}
                          onClick={() => {
                            navigate(item.path)
                            setDrawerOpen(false)
                          }}
                          className={cn(
                            'w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors',
                            isActive
                              ? 'bg-gold/10 text-gold border-r-2 border-gold'
                              : 'text-muted-foreground hover:bg-foreground/[0.03] hover:text-foreground'
                          )}
                        >
                          <Icon className="w-4 h-4 flex-shrink-0" />
                          <span className="text-sm font-medium flex-1">{item.label}</span>
                          {item.badge && (
                            <span className="px-1.5 py-0.5 rounded-full bg-red-500/20 text-red-400 text-[10px] font-bold">
                              {item.badge}
                            </span>
                          )}
                          {isActive && <ChevronRight className="w-3 h-3" />}
                        </button>
                      )
                    })}
                  </div>
                ))}
              </nav>

              {/* Drawer Footer */}
              <div className="border-t border-foreground/[0.06] p-4">
                <button
                  onClick={() => {
                    setDrawerOpen(false)
                    navigate('/')
                  }}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-foreground/[0.03] transition-colors text-muted-foreground"
                >
                  <ArrowLeft className="w-4 h-4" />
                  <span className="text-sm font-medium">返回玩家端</span>
                </button>
                <button className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-red-500/10 transition-colors text-red-400 mt-1">
                  <LogOut className="w-4 h-4" />
                  <span className="text-sm font-medium">登出</span>
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  )
}
